import React, { useState } from 'react';
import { Sparkle, Trash } from 'phosphor-react';
import ActionModal from './ActionModal';
import PrimaryButton from '../buttons/PrimaryButton';

/**
 * Motivos prontos + um campo livre. Os prontos existem porque é o que o usuário
 * responde de verdade num modal que ele não pediu para ver — e é o que a IA precisa
 * para saber se simplifica, se troca por algo mais barato ou se só encurta a rotina.
 */
const MOTIVOS = [
  'Irritou minha pele',
  'Muito caro',
  'Não tenho tempo',
  'Não vi resultado',
  'Acabou / não uso mais',
  'Outro',
];

interface StepRemovalReasonModalProps {
  isOpen: boolean;
  /** A etapa que está saindo, como o usuário a vê no cartão. */
  stepName: string;
  onConfirm: (reason: string, useAi: boolean) => void;
  onCancel: () => void;
}

export default function StepRemovalReasonModal({
  isOpen,
  stepName,
  onConfirm,
  onCancel,
}: StepRemovalReasonModalProps) {
  const [motivo, setMotivo] = useState<string>('');
  const [detalhe, setDetalhe] = useState<string>('');

  const fechar = () => {
    setMotivo('');
    setDetalhe('');
    onCancel();
  };

  const confirmar = (useAi: boolean) => {
    const texto = detalhe.trim() ? `${motivo} — ${detalhe.trim()}` : motivo;
    setMotivo('');
    setDetalhe('');
    // 500 é o teto do @Size no backend; cortar aqui evita um 422 por causa de digitação.
    onConfirm(texto.slice(0, 500), useAi);
  };

  return (
    <ActionModal isOpen={isOpen} onClose={fechar} title="Por que está removendo?" size="md">
      <div className="flex flex-col gap-4">
        <p className="text-xs text-nude-500 dark:text-gray-400 leading-relaxed">
          Você está tirando <strong className="text-gray-700 dark:text-gray-200">{stepName}</strong>.
          Contar o motivo deixa a IA reequilibrar o resto da rotina.
        </p>

        <div className="flex flex-wrap gap-2">
          {MOTIVOS.map((m) => (
            <button
              key={m}
              onClick={() => setMotivo(m)}
              className={`
                px-3 py-2 rounded-xl border-2 text-xs font-medium transition-colors active:scale-[0.97]
                ${motivo === m
                  ? 'border-blush-300 bg-blush-50 dark:bg-blush-900/20 text-blush-600 dark:text-blush-400'
                  : 'border-transparent bg-nude-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:border-blush-100 dark:hover:bg-gray-600'
                }
              `}
            >
              {m}
            </button>
          ))}
        </div>

        <div>
          <label
            htmlFor="removal-detail"
            className="text-xs font-semibold text-nude-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 block"
          >
            Conte mais — opcional
          </label>
          <textarea
            id="removal-detail"
            rows={3}
            maxLength={400}
            value={detalhe}
            onChange={(e) => setDetalhe(e.target.value)}
            placeholder="Ex: ardeu e ficou vermelho depois de dois dias"
            className="w-full px-4 py-3 rounded-xl border border-nude-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 placeholder-nude-300 focus:outline-none focus:ring-2 focus:ring-blush-300 resize-none"
          />
        </div>

        <div className="flex flex-col gap-2">
          <PrimaryButton
            fullWidth
            disabled={!motivo}
            onClick={() => confirmar(true)}
            icon={<Sparkle className="w-4 h-4" />}
          >
            Remover e ajustar com a IA
          </PrimaryButton>
          <button
            onClick={() => confirmar(false)}
            disabled={!motivo}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-nude-200 dark:border-gray-600 text-sm font-medium text-nude-500 dark:text-gray-400 hover:bg-nude-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <Trash className="w-4 h-4" />
            Só remover
          </button>
        </div>
      </div>
    </ActionModal>
  );
}
